import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import type {
  RelationshipHistoryEntry,
  SpousePartnerEdge,
} from '@/lib/familyUnits';

export type CanonicalTreeExternalEvidenceStatus =
  | 'UNAVAILABLE'
  | 'OWNER-SAFE'
  | 'GRAPH-SAFE-LEGACY'
  | 'UNSAFE';

export interface CanonicalTreeExternalEvidenceGatewayInput {
  readonly ownerUserId:
    string | null | undefined;

  readonly members:
    readonly GraphMember[];

  readonly spousePartnerEdges?:
    readonly SpousePartnerEdge[];

  readonly relationshipHistory?:
    readonly RelationshipHistoryEntry[];
}

export interface CanonicalTreeExternalEvidenceCollectionResult<T> {
  readonly status:
    CanonicalTreeExternalEvidenceStatus;

  readonly available:
    boolean;

  readonly accepted:
    readonly T[];

  readonly rejected:
    readonly T[];

  readonly reasons:
    readonly string[];
}

export interface CanonicalTreeExternalEvidenceGatewayResult {
  readonly ownerUserId:
    string | null;

  readonly memberIds:
    readonly string[];

  readonly spousePartnerEdges:
    CanonicalTreeExternalEvidenceCollectionResult<SpousePartnerEdge>;

  readonly relationshipHistory:
    CanonicalTreeExternalEvidenceCollectionResult<RelationshipHistoryEntry>;

  readonly readyForGateway:
    boolean;
}

const freezeCollection = <T>(
  result:
    CanonicalTreeExternalEvidenceCollectionResult<T>,
): CanonicalTreeExternalEvidenceCollectionResult<T> =>
  Object.freeze({
    ...result,

    accepted:
      Object.freeze([
        ...result.accepted,
      ]),

    rejected:
      Object.freeze([
        ...result.rejected,
      ]),

    reasons:
      Object.freeze([
        ...result.reasons,
      ]),
  });

const endpointsBelongToGraph = (
  memberIds:
    ReadonlySet<string>,

  personAId:
    string,

  personBId:
    string,
): boolean =>
  personAId !==
    personBId &&
  memberIds.has(
    personAId,
  ) &&
  memberIds.has(
    personBId,
  );

export const evaluateCanonicalTreeExternalEvidenceGatewayContract = (
  input:
    CanonicalTreeExternalEvidenceGatewayInput,
): CanonicalTreeExternalEvidenceGatewayResult => {
  const ownerUserId =
    input.ownerUserId ?? null;

  const memberIds =
    Array.from(
      new Set(
        input.members.map(
          member =>
            member.id,
        ),
      ),
    ).sort();

  const memberIdSet =
    new Set(
      memberIds,
    );

  const spouseInput =
    input.spousePartnerEdges;

  const spouseAccepted:
    SpousePartnerEdge[] = [];

  const spouseRejected:
    SpousePartnerEdge[] = [];

  let spouseHasLegacyOwner =
    false;

  if (spouseInput) {
    for (const edge of spouseInput) {
      const graphSafe =
        endpointsBelongToGraph(
          memberIdSet,
          edge.personAId,
          edge.personBId,
        );

      const ownerSafe =
        !!ownerUserId &&
        edge.ownerUserId ===
          ownerUserId;

      const legacyGraphSafe =
        edge.ownerUserId ===
          null &&
        graphSafe;

      if (
        graphSafe &&
        (
          ownerSafe ||
          legacyGraphSafe
        )
      ) {
        spouseAccepted.push(
          edge,
        );

        if (legacyGraphSafe) {
          spouseHasLegacyOwner =
            true;
        }
      } else {
        spouseRejected.push(
          edge,
        );
      }
    }
  }

  const spouseStatus:
    CanonicalTreeExternalEvidenceStatus =
      spouseInput ===
      undefined
        ? 'UNAVAILABLE'
        : spouseRejected.length > 0
          ? 'UNSAFE'
          : spouseHasLegacyOwner
            ? 'GRAPH-SAFE-LEGACY'
            : 'OWNER-SAFE';

  const spouseReasons:
    string[] = [];

  if (
    spouseStatus ===
    'UNAVAILABLE'
  ) {
    spouseReasons.push(
      'No spouse-partner evidence snapshot was supplied.',
    );
  }

  if (
    spouseStatus ===
    'GRAPH-SAFE-LEGACY'
  ) {
    spouseReasons.push(
      'Legacy null-owner rows were accepted only because both endpoints belong to the loaded graph.',
    );
  }

  if (
    spouseStatus ===
    'UNSAFE'
  ) {
    spouseReasons.push(
      'One or more spouse-partner rows failed owner or graph-endpoint validation.',
    );
  }

  const historyInput =
    input.relationshipHistory;

  const historyAccepted:
    RelationshipHistoryEntry[] = [];

  const historyRejected:
    RelationshipHistoryEntry[] = [];

  if (historyInput) {
    for (const entry of historyInput) {
      const graphSafe =
        endpointsBelongToGraph(
          memberIdSet,
          entry.personAId,
          entry.personBId,
        );

      const ownerSafe =
        !!ownerUserId &&
        entry.ownerUserId ===
          ownerUserId;

      if (
        graphSafe &&
        ownerSafe
      ) {
        historyAccepted.push(
          entry,
        );
      } else {
        historyRejected.push(
          entry,
        );
      }
    }
  }

  const historyStatus:
    CanonicalTreeExternalEvidenceStatus =
      historyInput ===
      undefined
        ? 'UNAVAILABLE'
        : historyRejected.length > 0
          ? 'UNSAFE'
          : 'OWNER-SAFE';

  const historyReasons:
    string[] = [];

  if (
    historyStatus ===
    'UNAVAILABLE'
  ) {
    historyReasons.push(
      'No relationship-history evidence snapshot was supplied.',
    );
  }

  if (
    historyStatus ===
    'UNSAFE'
  ) {
    historyReasons.push(
      'One or more relationship-history rows failed owner or graph-endpoint validation.',
    );
  }

  const spousePartnerEdges =
    freezeCollection({
      status:
        spouseStatus,

      available:
        spouseInput !==
        undefined,

      accepted:
        spouseAccepted,

      rejected:
        spouseRejected,

      reasons:
        spouseReasons,
    });

  const relationshipHistory =
    freezeCollection({
      status:
        historyStatus,

      available:
        historyInput !==
        undefined,

      accepted:
        historyAccepted,

      rejected:
        historyRejected,

      reasons:
        historyReasons,
    });

  return Object.freeze({
    ownerUserId,

    memberIds:
      Object.freeze(
        memberIds,
      ),

    spousePartnerEdges,

    relationshipHistory,

    readyForGateway:
      spousePartnerEdges
        .status !==
        'UNAVAILABLE' &&
      spousePartnerEdges
        .status !==
        'UNSAFE' &&
      relationshipHistory
        .status ===
        'OWNER-SAFE',
  });
};
